import { formatClock, formatTimecode } from '../lib/format';
import { Panel, Row, Switch } from './ui';
import { planTrim } from '../lib/timeline';
import type { Recording } from '../lib/engine';
import type { ClipPlayer } from '../lib/player';
import type { Settings } from '../settings';

interface TrimRange {
  inUs: number;
  outUs: number;
}

export function TrimPanel({
  recording,
  trim,
  onChange,
  settings,
  update,
  player,
}: {
  recording: Recording;
  trim: TrimRange;
  onChange: (trim: TrimRange) => void;
  settings: Settings;
  update: (patch: Partial<Settings>) => void;
  player: ClipPlayer | null;
}) {
  const plan = planTrim({
    videoChunks: recording.video.timeline,
    keyframes: recording.keyframes,
    audioChunks: recording.audio?.timeline ?? [],
    inUs: trim.inUs,
    outUs: trim.outUs,
    mode: 'fast',
  });
  const trimmed = trim.inUs > 0 || trim.outUs < recording.durationUs;
  const cutDuration = trim.outUs - trim.inUs;

  return (
    <Panel
      label="Trim"
      meta={<span className="chip chip--mono">{formatClock(cutDuration)}</span>}
    >
      <div className="readouts">
        <div className="readout">
          <span className="readout__label">in</span>
          <span className="readout__value">{formatTimecode(trim.inUs)}</span>
        </div>
        <div className="readout">
          <span className="readout__label">out</span>
          <span className="readout__value">{formatTimecode(trim.outUs)}</span>
        </div>
        <div className="readout">
          <span className="readout__label">source</span>
          <span className="readout__value">{formatTimecode(recording.durationUs)}</span>
        </div>
      </div>

      <Row label="Snap to keyframes" hint="Cuts land where a lossless export is possible">
        <Switch
          label="Snap to keyframes"
          checked={settings.snapToKeyframes}
          onChange={(value) => update({ snapToKeyframes: value })}
        />
      </Row>

      {plan.leadInUs > 0 && settings.mode === 'fast' ? (
        <p className="note note--warn">
          A lossless cut starts at the keyframe {formatTimecode(plan.leadInUs, false)} earlier —
          switch the export to <strong>Frame-accurate</strong> to cut exactly here.
        </p>
      ) : null}

      <div className="panel__actions">
        <button
          type="button"
          className="button button--ghost"
          disabled={!player}
          onClick={() =>
            onChange({
              inUs: Math.min(
                player?.store.getSnapshot().currentUs ?? 0,
                trim.outUs - recording.frameUs,
              ),
              outUs: trim.outUs,
            })
          }
        >
          Set in
        </button>
        <button
          type="button"
          className="button button--ghost"
          disabled={!player}
          onClick={() =>
            onChange({
              inUs: trim.inUs,
              outUs: Math.max(
                player?.store.getSnapshot().currentUs ?? recording.durationUs,
                trim.inUs + recording.frameUs,
              ),
            })
          }
        >
          Set out
        </button>
        <button
          type="button"
          className="button"
          onClick={() => onChange({ inUs: 0, outUs: recording.durationUs })}
          disabled={!trimmed}
        >
          Reset
        </button>
      </div>
    </Panel>
  );
}
